import type { OperatorRunStatus, OperatorRunTakeoverWire } from '~/hooks/queries/use-operator-runs';

/**
 * FRONT-1996 "Take the wheel" — pure decision logic for the Take over/Hand
 * back controls and their banner, consulted by both `operator-panel.tsx`
 * (the panel's header buttons + banner under it) and
 * `desktop-task-card.tsx` (the chat card's own footer controls), so the two
 * surfaces can never disagree about when a button is available or why it
 * isn't — same "one pure gate, one sweep test" shape as
 * `finish-now-gate.ts`/`smart-tagging-run-gate.ts`. CLAUDE.md's "a disabled
 * control and its explanation are one decision" rule: `disabled` is derived
 * FROM the reason below, never written independently of it, so a fifth
 * reason cannot be added without also writing its explanation.
 *
 * Both buttons only ever apply to a RUNNING run — a finished run has no
 * takeover left to start or end (the backend clears all five takeover
 * columns together in `finishRun`), so every branch below short-circuits on
 * `status !== 'running'` first, same as `stopControl` in
 * `desktop-task-card-model.ts`.
 */

export type OperatorTakeoverBanner =
  | { kind: 'none' }
  | { kind: 'needs-help'; reason: string }
  | { kind: 'in-control' };

export interface OperatorTakeoverButtonState {
  show: boolean;
  disabled: boolean;
  /** Why the button can't be pressed right now, as visible text (a
   *  tooltip) — never the only place the reason lives. `undefined` iff
   *  `disabled` is `false`. */
  reason?: string;
}

export interface OperatorTakeoverGateState {
  banner: OperatorTakeoverBanner;
  takeOver: OperatorTakeoverButtonState;
  handBack: OperatorTakeoverButtonState;
}

export interface OperatorTakeoverGateInputs {
  status: OperatorRunStatus | undefined;
  takeover: OperatorRunTakeoverWire | null | undefined;
  /** A `POST .../takeover` request for this run is in flight — on EITHER
   *  surface (card or panel), via `useOperatorTakeoverMutationsInFlight`,
   *  not merely this component's own mutation instance (FRONT-1996 review
   *  M7 — two surfaces, one shared mutation cache, so a click on either
   *  one disables both). */
  takingOver: boolean;
  /** A `POST .../hand-back` request for this run is in flight — same
   *  either-surface scope as `takingOver`. */
  handingBack: boolean;
  /** A `POST .../stop` request for this run is in flight (FRONT-1996
   *  review M11) — a run that is about to stop has nothing left to take
   *  over or hand back into, so both buttons disable with a reason rather
   *  than staying clickable right up until the stop actually lands. */
  stopping: boolean;
}

const HIDDEN: OperatorTakeoverButtonState = { show: false, disabled: false };

export function operatorTakeoverGate(input: OperatorTakeoverGateInputs): OperatorTakeoverGateState {
  const running = input.status === 'running';
  const takeover = running ? (input.takeover ?? null) : null;
  const active = takeover?.state === 'active';
  const requested = takeover?.state === 'requested';

  const banner: OperatorTakeoverBanner = active
    ? { kind: 'in-control' }
    : requested
      ? { kind: 'needs-help', reason: takeover?.reason ?? '' }
      : { kind: 'none' };

  // "Take over" confirms Astra's own request (requested -> active) just as
  // readily as it starts a fresh, owner-initiated one (none -> active) —
  // the backend's own `startTakeover` treats both the same way — so it
  // shows whenever the run is running and nobody is already active.
  // `stopping` is checked FIRST in both buttons — review M11 — since a run
  // about to stop should read as "stopping", not "taking over"/"handing
  // back", even if a takeover/hand-back request also happens to be in
  // flight at the same instant.
  const takeOver: OperatorTakeoverButtonState =
    !running || active
      ? HIDDEN
      : input.stopping
        ? { show: true, disabled: true, reason: 'Stopping…' }
        : input.takingOver
          ? { show: true, disabled: true, reason: 'Taking over…' }
          : input.handingBack
            ? { show: true, disabled: true, reason: 'Handing back…' }
            : { show: true, disabled: false };

  const handBack: OperatorTakeoverButtonState =
    !running || !active
      ? HIDDEN
      : input.stopping
        ? { show: true, disabled: true, reason: 'Stopping…' }
        : input.handingBack
          ? { show: true, disabled: true, reason: 'Handing back…' }
          : input.takingOver
            ? { show: true, disabled: true, reason: 'Taking over…' }
            : { show: true, disabled: false };

  return { banner, takeOver, handBack };
}
